/**
 * Converts frame number to pixel position
 */
export function frameToPixel(
  frame: number,
  zoom: number,
  scrollOffset: number = 0
): number {
  return frame * zoom - scrollOffset;
}

/**
 * Converts pixel position to frame number
 */
export function pixelToFrame(
  pixel: number,
  zoom: number,
  scrollOffset: number = 0
): number {
  return Math.round((pixel + scrollOffset) / zoom);
}

/**
 * Returns the layer's visibility range on the timeline.
 * ip/op define when the layer is visible; st only affects animation keyframe timing.
 */
export function getLayerTimeRange(layer: {
  ip: number;
  op: number;
  st?: number;
}): { start: number; end: number } {
  return {
    start: layer.ip,
    end: layer.op,
  };
}

/**
 * Formats frame number as timecode (MM:SS:FF)
 */
export function frameToTimecode(frame: number, frameRate: number): string {
  if (frameRate <= 0) return "00:00:00";
  const totalSeconds = Math.floor(frame / frameRate);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const frames = Math.floor(frame % frameRate);
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}:${frames.toString().padStart(2, "0")}`;
}

/**
 * Formats frame number as simple time (S.s or M:SS)
 */
export function frameToSimpleTime(frame: number, frameRate: number): string {
  if (frameRate <= 0) return "0";
  const totalSeconds = frame / frameRate;
  if (totalSeconds < 60) {
    return `${totalSeconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Determines appropriate tick interval based on zoom level
 */
function getAlignedMinorInterval(
  majorInterval: number,
  preferredSubdivisions: number
): number {
  const preferredInterval = Math.max(
    1,
    Math.round(majorInterval / preferredSubdivisions)
  );

  for (
    let interval = preferredInterval;
    interval <= majorInterval;
    interval++
  ) {
    if (majorInterval % interval === 0) {
      return interval;
    }
  }

  for (let interval = preferredInterval - 1; interval >= 1; interval--) {
    if (majorInterval % interval === 0) {
      return interval;
    }
  }

  return 1;
}

export function getTickInterval(
  zoom: number,
  frameRate: number
): {
  major: number;
  minor: number;
} {
  const pixelsPerSecond = zoom * frameRate;
  const framesPerSecond = Math.max(1, Math.round(frameRate));
  const oneSecond = framesPerSecond;
  const fiveSeconds = framesPerSecond * 5;

  if (pixelsPerSecond > 200) {
    return { major: oneSecond, minor: 1 }; // Every second, tick every frame
  } else if (pixelsPerSecond > 100) {
    return {
      major: oneSecond,
      minor: getAlignedMinorInterval(oneSecond, 5),
    };
  } else if (pixelsPerSecond > 50) {
    return {
      major: oneSecond,
      minor: getAlignedMinorInterval(oneSecond, 2),
    };
  } else {
    return { major: fiveSeconds, minor: oneSecond }; // Every 5 seconds
  }
}

/**
 * Clamps a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
